import debounce from "./debounce";

/**
 * Applies classes to the given textarea and its container based on the number
 * of lines in the textarea. The classes are determined by the given object
 * where the keys are the number of lines and the values are the class to apply.
 * If the number of lines is greater than the number of keys in the object, the
 * class for the maximum number of lines is used.
 *
 * @param {Object} options
 * @param {string} options.textarea - The selector for the textarea
 * @param {string} options.wrapper - The selector for the container for the
 *   textarea
 * @param {string} options.mainWrapper - The selector for the main application
 *   container
 * @param {Object} options.classes - The object mapping the number of lines to
 *   the class to apply
 */
const applyResizeClassOnInput = (options) => {
    if (!ApexChat.IsRoundedDynamicPlusTheme) {
        return
    }
    const { me = {}, textarea, wrapper, mainWrapper, classes, cleanup = false } = options;


    const debouncedAutoScroll = debounce(() => {
        me.autoScroll && me.autoScroll();
    }, 500); // Adjust delay as needed

    const textareaElement = me.$(textarea, me.context)?.[0];
    const wrapperElement = me.$(wrapper, me.context);
    const mainWrapperIds = mainWrapper.split(',');
    const domId = mainWrapperIds[0];
    const windowedId = mainWrapperIds[1];
    const exitpopupId = mainWrapperIds[2];
    let mainWrapperElement = me.$(domId, me.context);
    if (mainWrapperElement.length == 0) {
        mainWrapperElement = me.$(windowedId, me.context);
    } else if (mainWrapperElement.length == 0) {
        mainWrapperElement = me.$(exitpopupId, me.context);
    }
    if (!textareaElement) {
        console.log(`Textarea with selector '${textarea}' not found.`);
        return;
    }
    const removeAllClasses = () => {
        Object.values(classes).forEach(className => {
            textareaElement.classList.remove(className);
            wrapperElement.removeClass(`${className}_textarea_wrapper`);
            mainWrapperElement.removeClass(`${className}_app_wrapper`);
        });
    };

    // If cleanup is true, remove all classes and exit
    if (cleanup) {
        removeAllClasses();
        return;
    }
    // Helper function to count the number of lines in the textarea
    function getLineCount(textarea) {
        const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
        const contentHeight = textarea.scrollHeight;

        // Ensure a minimum of 1 line is counted, or 0 if the textarea is empty
        const lineCount = Math.max(1, Math.floor(contentHeight / lineHeight));

        // If the textarea is empty, return 1 (assuming 1 line for an empty textarea)
        if (!textarea.value.trim()) {
            return 1;
        }

        return lineCount;
    }

    // Update classes based on the number of lines
    function updateClasses() {
        const lineCount = getLineCount(textareaElement);
        // Remove all line-related classes from all elements
        Object.values(classes).forEach(className => {
            textareaElement.classList.remove(className);
            wrapperElement.removeClass(`${className}_textarea_wrapper`);
            mainWrapperElement.removeClass(`${className}_app_wrapper`);
        });

        // Add the appropriate class for the current line count
        if (classes[lineCount]) {
            textareaElement.classList.add(classes[lineCount]);
            wrapperElement.addClass(`${classes[lineCount]}_textarea_wrapper`);
            mainWrapperElement.addClass(`${classes[lineCount]}_app_wrapper`);
        } else if (lineCount > Object.keys(classes).length - 1) { // Dynamically determine the threshold for 'max'
            textareaElement.classList.add(classes.max);
            wrapperElement.addClass(`${classes.max}_textarea_wrapper`);
            mainWrapperElement.addClass(`${classes.max}_app_wrapper`);
        }
        debouncedAutoScroll();
    }

    // Attach event listener to update classes on input
    // textareaElement.addEventListener('input', updateClasses);

    // Initial update
    updateClasses();
}

export default applyResizeClassOnInput;